import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { TuningProfile, TuningProfileFieldChange } from '../../../../models/TuningProfile.model'
import { TuningProfileService } from '../../../../services/TuningProfileService/TuningProfileService'
import Loading from '../../../../components/Loading/Loading.component'
import {
  formatTuningValue,
  tuningProfileFieldLabels,
  tuningProfileSystemFieldLabels,
  tuningProfileTrailFields,
} from './tuningProfileFormatting'

type ApplyTuningProfileDialogProps = {
  // the profile to apply; the dialog is open while this is set
  profile: TuningProfile | null
  trailSystemName: string
  activeProfileName?: string
  isApplying: boolean
  onCancel: () => void
  onApply: (profile: TuningProfile) => void
}
/**
 * @description Confirms applying a tuning profile. Leads with a summary (the trail system's changes,
 * then how many trails change and a count per field) and keeps the per-trail list, one row per
 * trail, collapsed and filterable so systems with many trails stay short.
 */
const ApplyTuningProfileDialog = (props: ApplyTuningProfileDialogProps) => {
  const { profile, trailSystemName, activeProfileName, isApplying, onCancel, onApply } = props
  const [showTrails, setShowTrails] = useState(false)
  const [trailFilter, setTrailFilter] = useState('')

  useEffect(() => {
    if (profile) {
      setShowTrails(false)
      setTrailFilter('')
    }
  }, [profile])

  const {
    data: preview,
    isLoading,
    error,
  } = useQuery(
    ['TuningProfileService.preview', { id: profile?.id }],
    () => new TuningProfileService().preview(Number(profile?.id)),
    {
      enabled: !!profile,
      // always compare against the latest live values
      cacheTime: 0,
    },
  )

  const fieldCounts = useMemo(
    () =>
      tuningProfileTrailFields
        .map((field) => ({
          field,
          count: preview?.trails.filter((trail) => trail.changes.some((change) => change.field === field)).length ?? 0,
        }))
        .filter((fieldCount) => fieldCount.count > 0),
    [preview],
  )

  const filteredTrails = useMemo(() => {
    const filter = trailFilter.trim().toLowerCase()
    return (preview?.trails ?? []).filter((trail) => !filter || trail.name.toLowerCase().includes(filter))
  }, [preview, trailFilter])

  const hasChanges = !!preview && (preview.system.length > 0 || preview.trails.length > 0)
  const changeFor = (changes: TuningProfileFieldChange[], field: TuningProfileFieldChange['field']) =>
    changes.find((change) => change.field === field)

  return (
    <Dialog fullWidth maxWidth="md" open={!!profile} onClose={isApplying ? undefined : onCancel}>
      <DialogTitle>
        Apply {profile?.name} to {trailSystemName}?
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <Loading sx={{ py: 6 }} />}
        {!isLoading && !!error && (
          <Typography variant="error">{typeof error === 'string' ? error : 'Failed to load the changes.'}</Typography>
        )}
        {!isLoading && preview && (
          <Stack spacing={3}>
            {activeProfileName ? (
              <Typography variant="body2" color="grey.400">
                {activeProfileName} keeps its values, so you can switch back any time. Statuses update at the next
                hourly refresh.
              </Typography>
            ) : (
              <Alert severity="warning">
                No profile is active, so the current values aren&apos;t saved anywhere. Create a new profile first if
                you may want them back.
              </Alert>
            )}

            {!hasChanges && (
              <Typography variant="body1">Nothing changes: {profile?.name}&apos;s values are already live.</Typography>
            )}

            {preview.system.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="overline" color="grey.500">
                  Trail System
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {preview.system.map((change) => (
                    <Box key={change.field} sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'secondary.main' }}>
                      <Typography variant="body2" component="span">
                        {tuningProfileSystemFieldLabels[change.field]}{' '}
                      </Typography>
                      <Typography variant="body2" component="span" color="grey.500">
                        {formatTuningValue(change.field, change.current, 'trailSystem')} →{' '}
                      </Typography>
                      <Typography variant="body2" component="span" color="primary" fontWeight={700}>
                        {formatTuningValue(change.field, change.next, 'trailSystem')}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Typography variant="caption" color="grey.500">
                  Trails set to inherit from the system pick these up too.
                </Typography>
              </Stack>
            )}

            {preview.trails.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="overline" color="grey.500">
                  Trails
                </Typography>
                <Typography variant="body1">
                  <strong>
                    {preview.trails.length} of {preview.totalTrails}
                  </strong>{' '}
                  {preview.totalTrails === 1 ? 'trail' : 'trails'}{' '}
                  {preview.trails.length === 1 ? 'has its own values' : 'have their own values'} that change
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {fieldCounts.map((fieldCount) => (
                    <Chip
                      key={fieldCount.field}
                      size="small"
                      variant="outlined"
                      label={`${tuningProfileFieldLabels[fieldCount.field]} · ${fieldCount.count}`}
                    />
                  ))}
                </Stack>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Button
                    fullWidth
                    color="inherit"
                    onClick={() => setShowTrails((prev) => !prev)}
                    aria-expanded={showTrails}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{ transform: showTrails ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                      />
                    }
                    sx={{ justifyContent: 'space-between', px: 2, py: 1.25, textTransform: 'none' }}
                  >
                    {showTrails ? 'Hide Trail Changes' : `Show Trail Changes (${preview.trails.length})`}
                  </Button>
                  <Collapse in={showTrails} unmountOnExit>
                    <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Search Trails"
                        value={trailFilter}
                        onChange={(e) => setTrailFilter(e.target.value)}
                      />
                    </Box>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      {/* fixed layout so the four value columns share the width evenly whatever their contents;
                      on phones the table scrolls inside its box rather than squeezing the columns */}
                      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 600 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '24%' }}>Trail</TableCell>
                            {tuningProfileTrailFields.map((field) => (
                              <TableCell key={field} sx={{ width: '19%' }}>
                                {tuningProfileFieldLabels[field]}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredTrails.map((trail) => (
                            <TableRow key={trail.trailId}>
                              <TableCell sx={{ overflowWrap: 'anywhere' }}>{trail.name}</TableCell>
                              {tuningProfileTrailFields.map((field) => {
                                const change = changeFor(trail.changes, field)
                                return change ? (
                                  <TableCell key={field} sx={{ color: 'primary.main' }}>
                                    {formatTuningValue(field, change.current, 'trail')} →{' '}
                                    {formatTuningValue(field, change.next, 'trail')}
                                  </TableCell>
                                ) : (
                                  <TableCell key={field} sx={{ color: 'grey.500' }}>
                                    —
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          ))}
                          {!filteredTrails.length && (
                            <TableRow>
                              <TableCell colSpan={tuningProfileTrailFields.length + 1} sx={{ color: 'grey.500' }}>
                                No trails match.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isApplying}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!preview || isApplying}
          onClick={() => {
            if (profile) {
              onApply(profile)
            }
          }}
        >
          Apply {profile?.name}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
export default ApplyTuningProfileDialog
